import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    ArrowLeft, Download, Upload, Database, Folder, Key, AlertTriangle,
    CheckCircle2, Loader2, Copy, ShieldAlert,
} from 'lucide-react';
import { Toaster, toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Sauvegarde() {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [envVars, setEnvVars] = useState([]);
    const [loadingExport, setLoadingExport] = useState(false);
    const [loadingUploads, setLoadingUploads] = useState(false);
    const [restoring, setRestoring] = useState(false);
    const [restoreFile, setRestoreFile] = useState(null);
    const [restoreMode, setRestoreMode] = useState('merge');
    const [confirmRestore, setConfirmRestore] = useState(false);
    const [restoreSummary, setRestoreSummary] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [s, e] = await Promise.all([
                    axios.get(`${API_URL}/api/admin/backup/stats`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/admin/backup/env-template`, { withCredentials: true }),
                ]);
                setStats(s.data);
                setEnvVars(e.data?.variables || []);
            } catch (err) {
                toast.error(err?.response?.data?.detail || 'Erreur de chargement');
            }
        })();
    }, []);

    const downloadBlob = (data, filename, type = 'application/json') => {
        const blob = new Blob([data], { type });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const handleExport = async () => {
        setLoadingExport(true);
        try {
            const res = await axios.get(`${API_URL}/api/admin/backup/export`, {
                withCredentials: true, responseType: 'blob',
            });
            const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const a = document.createElement('a');
            a.href = URL.createObjectURL(res.data);
            a.download = `luxymove_backup_${ts}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(a.href);
            toast.success('Sauvegarde téléchargée — conservez le fichier en lieu sûr !');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur lors de l export');
        } finally {
            setLoadingExport(false);
        }
    };

    const handleExportUploads = async () => {
        setLoadingUploads(true);
        try {
            const res = await axios.get(`${API_URL}/api/admin/backup/uploads`, {
                withCredentials: true, responseType: 'blob',
            });
            const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const a = document.createElement('a');
            a.href = URL.createObjectURL(res.data);
            a.download = `luxymove_uploads_${ts}.tar.gz`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(a.href);
            toast.success('Archive fichiers téléchargée');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur lors du téléchargement');
        } finally {
            setLoadingUploads(false);
        }
    };

    const handleRestore = async () => {
        if (!restoreFile) return;
        setRestoring(true);
        setRestoreSummary(null);
        try {
            const fd = new FormData();
            fd.append('file', restoreFile);
            const res = await axios.post(
                `${API_URL}/api/admin/backup/restore?mode=${restoreMode}`,
                fd,
                { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
            );
            setRestoreSummary(res.data);
            toast.success('Restauration terminée');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur lors de la restauration');
        } finally {
            setRestoring(false);
            setConfirmRestore(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copié');
    };

    const presentCount = envVars.filter(v => v.present_in_preview).length;

    return (
        <Layout>
            <Toaster position="top-right" />
            <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/administration')} data-testid="back-btn">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Retour
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <Database className="w-6 h-6 text-[#1B365D]" />
                            Sauvegarde / Restauration
                        </h1>
                        <p className="text-sm text-gray-500">
                            Exportez toutes vos données avant un déploiement, ou restaurez-les après
                        </p>
                    </div>
                </div>

                {/* Critical warning */}
                <Card className="p-5 bg-red-50 border-red-200">
                    <div className="flex items-start gap-3">
                        <ShieldAlert className="w-6 h-6 text-red-600 mt-0.5 shrink-0" />
                        <div className="text-sm space-y-2">
                            <p className="font-bold text-red-900">À LIRE AVANT DE DÉPLOYER</p>
                            <p className="text-red-800">
                                La base de données de <strong>preview</strong> et celle de <strong>production</strong> sont
                                <strong> séparées</strong>. Lors du déploiement, la base de production démarre <strong>vide</strong>.
                            </p>
                            <p className="text-red-800">
                                <strong>Procédure obligatoire avant déploiement :</strong>
                            </p>
                            <ol className="list-decimal list-inside text-red-800 space-y-0.5 ml-2">
                                <li>Cliquez "Télécharger sauvegarde des données" (étape 1)</li>
                                <li>Cliquez "Télécharger les fichiers" (étape 2 — pièces jointes litiges, photos déménageurs, etc.)</li>
                                <li>Notez les variables d'environnement (étape 3) à recopier dans Emergent</li>
                                <li>Lancez le déploiement</li>
                                <li>Une fois en production, revenez ici → "Restaurer depuis sauvegarde" avec votre fichier .json</li>
                            </ol>
                        </div>
                    </div>
                </Card>

                {/* Step 1: Export data */}
                <Card className="p-6">
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold flex items-center justify-center shrink-0">
                            1
                        </div>
                        <div className="flex-1">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <Download className="w-5 h-5 text-blue-600" /> Télécharger sauvegarde des données
                            </h2>
                            <p className="text-sm text-gray-600 mt-1 mb-4">
                                Exporte toutes les données métier (prospects, clients, devis, factures, agenda, utilisateurs, etc.)
                                dans un fichier JSON unique. Conservez ce fichier en lieu sûr.
                            </p>
                            {stats && (
                                <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
                                    <p className="text-gray-700 mb-2">
                                        <strong>{stats.total}</strong> documents répartis sur {stats.collections.filter(c => c.count > 0).length} collections :
                                    </p>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-0.5 text-xs text-gray-600">
                                        {stats.collections.filter(c => c.count > 0).map(c => (
                                            <div key={c.collection} className="flex justify-between">
                                                <span>{c.collection}</span>
                                                <span className="font-mono font-semibold">{c.count}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <Button
                                onClick={handleExport}
                                disabled={loadingExport}
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                data-testid="backup-export-btn"
                            >
                                {loadingExport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                                Télécharger sauvegarde JSON
                            </Button>
                        </div>
                    </div>
                </Card>

                {/* Step 2: Export uploads */}
                <Card className="p-6">
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-700 font-bold flex items-center justify-center shrink-0">
                            2
                        </div>
                        <div className="flex-1">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <Folder className="w-5 h-5 text-purple-600" /> Télécharger les fichiers
                            </h2>
                            <p className="text-sm text-gray-600 mt-1 mb-4">
                                Archive .tar.gz contenant les pièces jointes : photos litiges, justificatifs paiement,
                                photos déménageurs, fichiers tâches, lettres de voiture PDF, etc.
                            </p>
                            <Button
                                onClick={handleExportUploads}
                                disabled={loadingUploads}
                                variant="outline"
                                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                                data-testid="backup-uploads-btn"
                            >
                                {loadingUploads ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Folder className="w-4 h-4 mr-2" />}
                                Télécharger archive fichiers
                            </Button>
                        </div>
                    </div>
                </Card>

                {/* Step 3: Env vars */}
                <Card className="p-6">
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 font-bold flex items-center justify-center shrink-0">
                            3
                        </div>
                        <div className="flex-1">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <Key className="w-5 h-5 text-amber-600" /> Variables d'environnement à recopier
                            </h2>
                            <p className="text-sm text-gray-600 mt-1 mb-4">
                                {presentCount} variable(s) configurée(s) actuellement. Les valeurs <strong>doivent être recopiées identiques</strong>
                                {' '}dans l'interface Emergent au moment du déploiement, sinon les intégrations (OVH, site web, emails) cesseront
                                de fonctionner. <strong>Pour des raisons de sécurité, les valeurs ne sont jamais affichées en clair</strong> — vous
                                devez les retrouver dans l'onglet "Environment" de votre pod Emergent.
                            </p>
                            <div className="space-y-1.5">
                                {envVars.map(v => (
                                    <div key={v.key} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm">
                                        {v.present_in_preview ? (
                                            <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                                        ) : (
                                            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                                        )}
                                        <code className="font-mono font-semibold text-gray-900 flex-shrink-0">{v.key}</code>
                                        <button
                                            onClick={() => copyToClipboard(v.key)}
                                            className="p-1 text-gray-400 hover:text-gray-700"
                                            title="Copier le nom"
                                        >
                                            <Copy className="w-3.5 h-3.5" />
                                        </button>
                                        {v.masked_preview && (
                                            <Badge variant="outline" className="text-xs font-mono">
                                                {v.masked_preview}
                                            </Badge>
                                        )}
                                        <span className="text-xs text-gray-500 truncate ml-auto">{v.hint}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Step 4: Restore */}
                <Card className="p-6 border-emerald-200 bg-emerald-50/30">
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 font-bold flex items-center justify-center shrink-0">
                            4
                        </div>
                        <div className="flex-1">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                <Upload className="w-5 h-5 text-emerald-600" /> Restaurer depuis sauvegarde
                            </h2>
                            <p className="text-sm text-gray-600 mt-1 mb-4">
                                À utiliser <strong>uniquement après déploiement en production</strong>. Sélectionnez le fichier .json
                                exporté à l'étape 1.
                            </p>
                            <div className="space-y-3">
                                <input
                                    type="file"
                                    accept=".json,application/json"
                                    onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                                    className="text-sm w-full file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-100 file:text-emerald-700 hover:file:bg-emerald-200"
                                    data-testid="restore-file-input"
                                />
                                {restoreFile && (
                                    <div className="text-sm text-gray-600">
                                        Fichier sélectionné : <code className="bg-white px-2 py-0.5 rounded">{restoreFile.name}</code>
                                        {' '}({(restoreFile.size / 1024).toFixed(1)} KB)
                                    </div>
                                )}
                                <div className="flex gap-2 items-center">
                                    <label className="text-sm font-medium text-gray-700">Mode :</label>
                                    <label className="text-sm flex items-center gap-1">
                                        <input type="radio" name="mode" value="merge" checked={restoreMode === 'merge'} onChange={() => setRestoreMode('merge')} />
                                        <span>Fusion (insère seulement les nouveaux)</span>
                                    </label>
                                    <label className="text-sm flex items-center gap-1">
                                        <input type="radio" name="mode" value="replace" checked={restoreMode === 'replace'} onChange={() => setRestoreMode('replace')} />
                                        <span className="text-red-700 font-medium">Remplacer (efface tout d'abord)</span>
                                    </label>
                                </div>
                                <Button
                                    onClick={() => setConfirmRestore(true)}
                                    disabled={!restoreFile || restoring}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                    data-testid="restore-btn"
                                >
                                    {restoring ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                                    Lancer la restauration
                                </Button>
                            </div>

                            {restoreSummary && (
                                <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                                    <p className="text-sm font-semibold text-gray-900 mb-2">
                                        ✅ Restauration terminée ({restoreSummary.mode})
                                    </p>
                                    <div className="text-xs grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 max-h-60 overflow-auto">
                                        {Object.entries(restoreSummary.collections || {}).map(([col, info]) => (
                                            <div key={col} className="flex justify-between text-gray-600">
                                                <span>{col}</span>
                                                {info.skipped ? (
                                                    <span className="text-amber-600">{info.reason}</span>
                                                ) : (
                                                    <span><span className="text-green-700 font-mono">{info.inserted}</span>/{info.total_in_file}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Card>
            </div>

            <AlertDialog open={confirmRestore} onOpenChange={setConfirmRestore}>
                <AlertDialogContent data-testid="confirm-restore-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-amber-700">
                            Confirmer la restauration ?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {restoreMode === 'replace'
                                ? <span className="text-red-700 font-semibold">⚠️ Mode REMPLACER : toutes les données actuelles seront supprimées et remplacées par celles du fichier. Cette action est irréversible.</span>
                                : 'Mode Fusion : les documents existants seront préservés, seuls les nouveaux seront insérés.'}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="confirm-restore-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleRestore}
                            className={restoreMode === 'replace' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                            data-testid="confirm-restore-confirm"
                        >
                            Confirmer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
